import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Calculator, 
  FileText,
  TrendingDown, 
  Euro,
  CheckCircle,
  AlertCircle,
  Save,
  Download,
  Trash2,
  Edit,
  Plus,
  Upload,
  Eye,
  EyeOff
} from "lucide-react";
import { toast } from "sonner";
import SirenSearch from "./SirenSearch";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SimulatorPage = () => {
  // États du formulaire
  const [formData, setFormData] = useState({
    raison_sociale: "",
    siren: "", // Changed from siret
    interlocuteur: "",
    fonction: "",
    telephone: "",
    adresse_site: "",
    code_postal: "",
    ville: "",
    segment: "C5_BASE",
    date_debut_contrat: "",
    date_fin_contrat: "",
    duree_contrat_mois: 36,
    puissance_kva: "",
    pdl: "",
    pce: "",
    car: "",
    // CAR détaillé selon segments
    // C5_HPHC (2 champs)
    car_hp: "",
    car_hc: "",
    // C4 et C2 (4-5 champs)
    car_pointe: "", // C2 uniquement
    car_hph: "",
    car_hch: "",
    car_hpe: "",
    car_hce: "",
    // C5_BASE (1 champ)
    car_base: "",
    // GAZ (1 champ)
    car_gaz: "",
    // Fournisseurs - ACTUEL MANUEL
    fournisseur_actuel_nom: "", // Nom manuel
    fournisseur_actuel_logo: null, // Logo uploadé
    // Fournisseur FUTUR
    fournisseur_futur_id: "",
    // Abonnements
    abonnement_actuel_mensuel: "",
    abonnement_futur_mensuel: "",
    // Prix MGW actuel (selon segments)
    // C5_HPHC (2 champs)
    prix_actuel_hp: "",
    prix_actuel_hc: "",
    // C4 et C2 (4-5 champs)
    prix_actuel_pointe: "", // C2 uniquement
    prix_actuel_hph: "",
    prix_actuel_hch: "",
    prix_actuel_hpe: "",
    prix_actuel_hce: "",
    // C5_BASE (1 champ)
    prix_actuel_base: "",
    // GAZ (1 champ)
    prix_actuel_gaz: "",
    // Prix MGW futur (selon segments)
    // C5_HPHC (2 champs)
    prix_futur_hp: "",
    prix_futur_hc: "",
    // C4 et C2 (4-5 champs)
    prix_futur_pointe: "", // C2 uniquement
    prix_futur_hph: "",
    prix_futur_hch: "",
    prix_futur_hpe: "",
    prix_futur_hce: "",
    // C5_BASE (1 champ)
    prix_futur_base: "",
    // GAZ (1 champ)
    prix_futur_gaz: "",
    // Marges selon segments
    // C5 HP/HC (2 champs)
    marge_hp: "",
    marge_hc: "",
    // C4 et C2 (4-5 champs)
    marge_pointe: "", // C2 uniquement
    marge_hph: "",
    marge_hch: "",
    marge_hpe: "",
    marge_hce: "",
    // C5 BASE (1 champ)
    marge_base: "",
    // GAZ (1 champ)
    marge_gaz: "",
    // Scoring
    score_ellipro: "",
    score_credit_safe: ""
  });

  const [suppliers, setSuppliers] = useState([]);
  const [simulations, setSimulations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingSimulations, setLoadingSimulations] = useState(true);
  const [editingSimulationId, setEditingSimulationId] = useState(null);
  const [isDragging, setIsDragging] = useState(false); // Pour le drag & drop
  
  // Filtres pour les simulations
  const [filters, setFilters] = useState({
    search: "",
    segment: "all",
    dateFrom: "",
    dateTo: ""
  });
  
  // État pour gérer l'onglet actif
  const [activeTab, setActiveTab] = useState("new-simulation");
  
  // État pour la visibilité de la commission (masquée par défaut)
  const [showCommission, setShowCommission] = useState(false);
  
  // Calculs en temps réel
  const [calculatedResults, setCalculatedResults] = useState(null);
  const [recommendation, setRecommendation] = useState(null);

  // Définir toutes les fonctions AVANT les useEffect
  const fetchSuppliers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/config/suppliers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuppliers(response.data.filter(s => s.is_active));
    } catch (error) {
      console.error("Error fetching suppliers:", error);
      toast.error("Erreur lors du chargement des fournisseurs");
    }
  };

  const fetchSimulations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/simulations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSimulations(response.data);
    } catch (error) {
      console.error("Error fetching simulations:", error);
      toast.error("Erreur lors du chargement des simulations");
    } finally {
      setLoadingSimulations(false);
    }
  };

  const calculateRecommendation = () => {
    const { segment, puissance_kva, score_ellipro, score_credit_safe } = formData;
    
    // GAZEL: C4 ou C2 avec puissance > 36 KVA
    if ((segment === "C4" || segment === "C2") && parseFloat(puissance_kva) > 36) {
      const score = score_credit_safe || 50;
      setRecommendation({
        fournisseur: "GAZEL",
        fournisseur_id: null, // On cherchera l'ID après
        raison: `Segment ${segment} avec puissance ${puissance_kva} KVA (>36). Score Credit Safe : ${score}/100`,
        modalites: "Paiement 100% à la signature du contrat"
      });
      return;
    }

    // OHM: Priorité C5 ET score >= 5, OU autres segments avec score >= 5
    // IMPORTANT: Si score < 5, c'est JPME
    if (score_ellipro && parseFloat(score_ellipro) >= 5) {
      setRecommendation({
        fournisseur: "OHM",
        fournisseur_id: null,
        raison: `Segment ${segment} avec score Ellipro ${score_ellipro}/10 (≥5)`,
        modalites: "50% le 15 du mois suivant la signature, 50% le 15 du mois de début contrat"
      });
      return;
    }
    
    // C5 sans score ou score >= 5 → OHM par défaut
    if (segment.startsWith("C5") && (!score_ellipro || parseFloat(score_ellipro) >= 5)) {
      setRecommendation({
        fournisseur: "OHM",
        fournisseur_id: null,
        raison: `Segment ${segment} (priorité OHM)${score_ellipro ? `, score Ellipro ${score_ellipro}/10` : ''}`,
        modalites: "50% le 15 du mois suivant la signature, 50% le 15 du mois de début contrat"
      });
      return;
    }

    // JPME: Par défaut (score < 5 ou autres cas)
    setRecommendation({
      fournisseur: "JPME",
      fournisseur_id: null,
      raison: `Segment ${segment}${score_ellipro ? ` - Score Ellipro ${score_ellipro}/10 (<5)` : ' - Fournisseur par défaut'}`,
      modalites: "10% upfront, le reste mensualisé sur la durée du contrat"
    });
  };

  const calculateBudget = (abonnement, prices, segment) => {
    let budget = parseFloat(abonnement || 0) * 12;
    
    // C5 HP/HC (2 champs : HP et HC)
    if (segment === "C5_HPHC") {
      budget += (parseFloat(formData.car_hp || 0) * parseFloat(prices.hp || 0));
      budget += (parseFloat(formData.car_hc || 0) * parseFloat(prices.hc || 0));
    }
    // C4 (4 champs : HPH, HCH, HPE, HCE)
    else if (segment === "C4") {
      budget += (parseFloat(formData.car_hph || 0) * parseFloat(prices.hph || 0));
      budget += (parseFloat(formData.car_hch || 0) * parseFloat(prices.hch || 0));
      budget += (parseFloat(formData.car_hpe || 0) * parseFloat(prices.hpe || 0));
      budget += (parseFloat(formData.car_hce || 0) * parseFloat(prices.hce || 0));
    }
    // C2 (5 champs : POINTE, HPH, HCH, HPE, HCE)
    else if (segment === "C2") {
      budget += (parseFloat(formData.car_pointe || 0) * parseFloat(prices.pointe || 0));
      budget += (parseFloat(formData.car_hph || 0) * parseFloat(prices.hph || 0));
      budget += (parseFloat(formData.car_hch || 0) * parseFloat(prices.hch || 0));
      budget += (parseFloat(formData.car_hpe || 0) * parseFloat(prices.hpe || 0));
      budget += (parseFloat(formData.car_hce || 0) * parseFloat(prices.hce || 0));
    }
    // C5 BASE (1 champ : Base)
    else if (segment === "C5_BASE") {
      budget += (parseFloat(formData.car_base || 0) * parseFloat(prices.base || 0));
    }
    // GAZ (1 champ : Gaz)
    else if (segment === "GAZ") {
      budget += (parseFloat(formData.car_gaz || 0) * parseFloat(prices.gaz || 0));
    }
    
    return budget;
  };

  const calculateResults = () => {
    if (!formData.car && !formData.car_base && !formData.car_gaz && 
        !formData.car_hp && !formData.car_hph && !formData.car_pointe) {
      setCalculatedResults(null);
      return;
    }

    const pricesActuel = {
      hp: formData.prix_actuel_hp,
      hc: formData.prix_actuel_hc,
      pointe: formData.prix_actuel_pointe,
      hph: formData.prix_actuel_hph,
      hch: formData.prix_actuel_hch,
      hpe: formData.prix_actuel_hpe,
      hce: formData.prix_actuel_hce,
      base: formData.prix_actuel_base,
      gaz: formData.prix_actuel_gaz
    };

    const pricesFutur = {
      hp: formData.prix_futur_hp,
      hc: formData.prix_futur_hc,
      pointe: formData.prix_futur_pointe,
      hph: formData.prix_futur_hph,
      hch: formData.prix_futur_hch,
      hpe: formData.prix_futur_hpe,
      hce: formData.prix_futur_hce,
      base: formData.prix_futur_base,
      gaz: formData.prix_futur_gaz
    };

    const budgetActuel = calculateBudget(formData.abonnement_actuel_mensuel, pricesActuel, formData.segment);
    const budgetFutur = calculateBudget(formData.abonnement_futur_mensuel, pricesFutur, formData.segment);
    
    const economieAnnuelle = budgetActuel - budgetFutur;
    const nbAnnees = (parseFloat(formData.duree_contrat_mois) || 36) / 12;
    const economieGlobale = economieAnnuelle * nbAnnees;
    const variation = budgetActuel > 0 ? (economieAnnuelle / budgetActuel) * 100 : 0;
    
    // Commission = Somme (CAR_segment × Marge_segment) × Nb années × 0.5
    let commission = 0;
    
    if (formData.segment === "C5_HPHC") {
      const car_hp = parseFloat(formData.car_hp || 0);
      const car_hc = parseFloat(formData.car_hc || 0);
      const marge_hp = parseFloat(formData.marge_hp || 0);
      const marge_hc = parseFloat(formData.marge_hc || 0);
      commission = ((car_hp * marge_hp) + (car_hc * marge_hc)) * nbAnnees * 0.5;
    } else if (formData.segment === "C4") {
      const car_hph = parseFloat(formData.car_hph || 0);
      const car_hch = parseFloat(formData.car_hch || 0);
      const car_hpe = parseFloat(formData.car_hpe || 0);
      const car_hce = parseFloat(formData.car_hce || 0);
      const marge_hph = parseFloat(formData.marge_hph || 0);
      const marge_hch = parseFloat(formData.marge_hch || 0);
      const marge_hpe = parseFloat(formData.marge_hpe || 0);
      const marge_hce = parseFloat(formData.marge_hce || 0);
      commission = ((car_hph * marge_hph) + (car_hch * marge_hch) + (car_hpe * marge_hpe) + (car_hce * marge_hce)) * nbAnnees * 0.5;
    } else if (formData.segment === "C2") {
      const car_pointe = parseFloat(formData.car_pointe || 0);
      const car_hph = parseFloat(formData.car_hph || 0);
      const car_hch = parseFloat(formData.car_hch || 0);
      const car_hpe = parseFloat(formData.car_hpe || 0);
      const car_hce = parseFloat(formData.car_hce || 0);
      const marge_pointe = parseFloat(formData.marge_pointe || 0);
      const marge_hph = parseFloat(formData.marge_hph || 0);
      const marge_hch = parseFloat(formData.marge_hch || 0);
      const marge_hpe = parseFloat(formData.marge_hpe || 0);
      const marge_hce = parseFloat(formData.marge_hce || 0);
      commission = ((car_pointe * marge_pointe) + (car_hph * marge_hph) + (car_hch * marge_hch) + (car_hpe * marge_hpe) + (car_hce * marge_hce)) * nbAnnees * 0.5;
    } else if (formData.segment === "C5_BASE") {
      const car_base = parseFloat(formData.car_base || 0);
      const marge_base = parseFloat(formData.marge_base || 0);
      commission = (car_base * marge_base) * nbAnnees * 0.5;
    } else if (formData.segment === "GAZ") {
      const car_gaz = parseFloat(formData.car_gaz || 0);
      const marge_gaz = parseFloat(formData.marge_gaz || 0);
      commission = (car_gaz * marge_gaz) * nbAnnees * 0.5;
    }

    setCalculatedResults({
      budget_actuel: budgetActuel,
      budget_futur: budgetFutur,
      economie_annuelle: economieAnnuelle,
      economie_globale: economieGlobale,
      variation_pourcentage: variation,
      commission: commission
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSirenCompanySelect = (companyData) => {
    // Remplir automatiquement les champs depuis les données SIREN
    // Note: SirenSearch envoie les données avec ces clés:
    // raison_sociale, adresse, code_postal, ville, nom, prenom, fonction
    console.log("Company data received:", companyData);
    
    setFormData(prev => ({
      ...prev,
      siren: companyData.siren,
      raison_sociale: companyData.raison_sociale || companyData.company_name || "",
      interlocuteur: companyData.prenom && companyData.nom 
        ? `${companyData.prenom} ${companyData.nom}` 
        : prev.interlocuteur,
      fonction: companyData.fonction || prev.fonction,
      adresse_site: companyData.adresse || companyData.address || "",
      code_postal: companyData.code_postal || companyData.postal_code || "",
      ville: companyData.ville || companyData.city || ""
    }));
    toast.success("Informations entreprise chargées automatiquement");
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Vérifier le type de fichier
      if (!file.type.startsWith('image/')) {
        toast.error("Veuillez sélectionner une image");
        return;
      }
      // Créer une URL preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          fournisseur_actuel_logo: reader.result // Base64 string
        }));
        toast.success("Logo chargé");
      };
      reader.readAsDataURL(file);
    }
  };

  const getP0ForSegment = (supplier, segment, field = null) => {
    // Récupère le P0 approprié selon le segment et le champ
    if (!supplier || !supplier.p0_prices) {
      // Fallback sur l'ancien champ unique
      return parseFloat(supplier?.p0_margin || supplier?.p0 || supplier?.margin_rate || 0);
    }
    
    const p0_prices = supplier.p0_prices;
    
    // Si un champ spécifique est demandé (ex: "hp", "hch"), le retourner
    if (field && p0_prices[field]) {
      return parseFloat(p0_prices[field] || 0);
    }
    
    // Sinon, retourner le P0 selon le segment (pour affichage général)
    if (segment === "C5_BASE") {
      return parseFloat(p0_prices.base || 0);
    } else if (segment === "C5_HPHC") {
      // Moyenne HP/HC pour affichage
      return (parseFloat(p0_prices.hp || 0) + parseFloat(p0_prices.hc || 0)) / 2;
    } else if (segment === "C4") {
      // Moyenne des 4 index
      return (parseFloat(p0_prices.hph || 0) + parseFloat(p0_prices.hch || 0) + 
              parseFloat(p0_prices.hpe || 0) + parseFloat(p0_prices.hce || 0)) / 4;
    } else if (segment === "C2") {
      // Moyenne des 5 index
      return (parseFloat(p0_prices.pointe || 0) + parseFloat(p0_prices.hph || 0) + 
              parseFloat(p0_prices.hch || 0) + parseFloat(p0_prices.hpe || 0) + 
              parseFloat(p0_prices.hce || 0)) / 5;
    } else if (segment === "GAZ") {
      return parseFloat(p0_prices.gaz || 0);
    }
    
    // Fallback
    return parseFloat(p0_prices.base || p0_prices.hp || 0);
  };

  const getMargeForField = (field) => {
    // Récupère la marge appropriée selon le champ
    const margeField = `marge_${field}`;
    return parseFloat(formData[margeField] || 0);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      // Vérifier le type de fichier
      if (!file.type.startsWith('image/')) {
        toast.error("Veuillez déposer une image");
        return;
      }
      // Créer une URL preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          fournisseur_actuel_logo: reader.result // Base64 string
        }));
        toast.success("Logo chargé");
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCalculateRecommandation = () => {
    const { segment, puissance_kva, score_ellipro, score_credit_safe } = formData;
    
    let fournisseurRecommande = null;
    let raison = "";
    let modalites = "";
    let abonnementFutur = 0;
    
    // ============ NOUVELLE LOGIQUE DE RECOMMANDATION ============
    
    // CAS SPÉCIAL GAZ
    if (segment === "GAZ") {
      // Vérifier les scores pour le gaz
      const scoreElliproNum = parseFloat(score_ellipro) || 0;
      const scoreCreditSafeNum = parseFloat(score_credit_safe) || 0;
      
      if (scoreElliproNum < 5 || scoreCreditSafeNum < 35) {
        // Cotation gaz impossible
        setRecommendation({
          fournisseur: "IMPOSSIBLE",
          fournisseur_id: null,
          raison: `Cotation GAZ impossible - Score trop bas (Ellipro: ${scoreElliproNum}/10, Credit Safe: ${scoreCreditSafeNum}/100). Minimum requis: Ellipro ≥5 OU Credit Safe ≥35`,
          modalites: "Aucune offre disponible"
        });
        toast.error("❌ Cotation GAZ impossible avec ces scores");
        return;
      }
      
      // Si score OK pour le gaz → OHM uniquement (JPME ne fait pas de gaz)
      if (scoreElliproNum >= 5) {
        fournisseurRecommande = "OHM";
        raison = `Segment GAZ avec score Ellipro ${scoreElliproNum}/10 (≥5)`;
        modalites = "50% le 15 du mois suivant la signature, 50% le 15 du mois de début contrat";
        abonnementFutur = 0; // OHM: 0€/an
      }
    }
    // GAZEL: C4 uniquement ET Credit Safe ≥ 35/100
    else if (segment === "C4") {
      const scoreCreditSafeNum = parseFloat(score_credit_safe) || 0;
      
      if (scoreCreditSafeNum >= 35) {
        fournisseurRecommande = "GAZEL";
        raison = `Segment C4 avec Credit Safe ${scoreCreditSafeNum}/100 (≥35)`;
        modalites = "Paiement 100% à la signature du contrat";
        abonnementFutur = 0; // GAZEL: 0€/an
      } else {
        // Score trop bas pour GAZEL, essayer OHM ou JPME
        const scoreElliproNum = parseFloat(score_ellipro) || 0;
        if (scoreElliproNum >= 5) {
          fournisseurRecommande = "OHM";
          raison = `C4 avec Credit Safe ${scoreCreditSafeNum}/100 (<35, GAZEL non éligible). Score Ellipro ${scoreElliproNum}/10 (≥5)`;
          modalites = "50% le 15 du mois suivant la signature, 50% le 15 du mois de début contrat";
          abonnementFutur = 0; // OHM: 0€/an
        } else {
          fournisseurRecommande = "JPME";
          raison = `C4 avec scores insuffisants pour GAZEL et OHM (Credit Safe: ${scoreCreditSafeNum}/100, Ellipro: ${scoreElliproNum}/10)`;
          modalites = "10% upfront, le reste mensualisé sur la durée du contrat";
          abonnementFutur = 120; // JPME: 120€/an
        }
      }
    }
    // OHM: Score Ellipro ≥ 5
    else if (score_ellipro && parseFloat(score_ellipro) >= 5) {
      fournisseurRecommande = "OHM";
      raison = `Segment ${segment} avec score Ellipro ${score_ellipro}/10 (≥5)`;
      modalites = "50% le 15 du mois suivant la signature, 50% le 15 du mois de début contrat";
      abonnementFutur = 0; // OHM: 0€/an
    }
    // C5 sans score → OHM par défaut (priorité C5)
    else if (segment.startsWith("C5") && !score_ellipro) {
      fournisseurRecommande = "OHM";
      raison = `Segment ${segment} (priorité OHM, score non fourni)`;
      modalites = "50% le 15 du mois suivant la signature, 50% le 15 du mois de début contrat";
      abonnementFutur = 0; // OHM: 0€/an
    }
    // JPME: Par défaut (score < 5 ou autres cas - SAUF GAZ)
    else {
      if (segment === "GAZ") {
        // Déjà géré plus haut
        return;
      }
      fournisseurRecommande = "JPME";
      raison = `Segment ${segment}${score_ellipro ? ` - Score Ellipro ${score_ellipro}/10 (<5)` : ' - Fournisseur par défaut'}`;
      modalites = "10% upfront, le reste mensualisé sur la durée du contrat";
      abonnementFutur = 120; // JPME: 120€/an
    }
    
    // Si aucun fournisseur trouvé (ne devrait pas arriver)
    if (!fournisseurRecommande) {
      toast.error("Aucun fournisseur éligible trouvé");
      return;
    }
    
    // Mettre à jour la recommandation
    setRecommendation({
      fournisseur: fournisseurRecommande,
      fournisseur_id: null,
      raison: raison,
      modalites: modalites
    });
    
    // Chercher le fournisseur dans la liste
    const supplierFound = suppliers.find(s => 
      s.name.toUpperCase().includes(fournisseurRecommande.toUpperCase())
    );
    
    if (supplierFound) {
      // Calculer les prix MGW selon le segment avec les P0 et marges spécifiques
      
      // Mettre à jour le formulaire avec le fournisseur, l'abonnement et les prix
      const updates = {
        fournisseur_futur_id: supplierFound.id,
        abonnement_futur_mensuel: (abonnementFutur / 12).toFixed(2) // Convertir en mensuel
      };
      
      // Selon le segment, mettre à jour les prix MGW avec P0 + Marge spécifiques par champ
      if (segment === "C5_HPHC") {
        // C5 HP/HC : 2 champs avec P0 et marge spécifiques
        updates.prix_futur_hp = (getP0ForSegment(supplierFound, segment, 'hp') + getMargeForField('hp')).toFixed(2);
        updates.prix_futur_hc = (getP0ForSegment(supplierFound, segment, 'hc') + getMargeForField('hc')).toFixed(2);
      } else if (segment === "C4") {
        // C4 : 4 champs avec P0 et marge spécifiques
        updates.prix_futur_hph = (getP0ForSegment(supplierFound, segment, 'hph') + getMargeForField('hph')).toFixed(2);
        updates.prix_futur_hch = (getP0ForSegment(supplierFound, segment, 'hch') + getMargeForField('hch')).toFixed(2);
        updates.prix_futur_hpe = (getP0ForSegment(supplierFound, segment, 'hpe') + getMargeForField('hpe')).toFixed(2);
        updates.prix_futur_hce = (getP0ForSegment(supplierFound, segment, 'hce') + getMargeForField('hce')).toFixed(2);
      } else if (segment === "C2") {
        // C2 : 5 champs avec P0 et marge spécifiques
        updates.prix_futur_pointe = (getP0ForSegment(supplierFound, segment, 'pointe') + getMargeForField('pointe')).toFixed(2);
        updates.prix_futur_hph = (getP0ForSegment(supplierFound, segment, 'hph') + getMargeForField('hph')).toFixed(2);
        updates.prix_futur_hch = (getP0ForSegment(supplierFound, segment, 'hch') + getMargeForField('hch')).toFixed(2);
        updates.prix_futur_hpe = (getP0ForSegment(supplierFound, segment, 'hpe') + getMargeForField('hpe')).toFixed(2);
        updates.prix_futur_hce = (getP0ForSegment(supplierFound, segment, 'hce') + getMargeForField('hce')).toFixed(2);
      } else if (segment === "C5_BASE") {
        // C5 BASE : 1 champ avec P0 et marge spécifiques
        updates.prix_futur_base = (getP0ForSegment(supplierFound, segment, 'base') + getMargeForField('base')).toFixed(2);
      } else if (segment === "GAZ") {
        // GAZ : 1 champ avec P0 et marge spécifiques
        updates.prix_futur_gaz = (getP0ForSegment(supplierFound, segment, 'gaz') + getMargeForField('gaz')).toFixed(2);
      }
      
      setFormData(prev => ({
        ...prev,
        ...updates
      }));
      
      const p0Moyen = getP0ForSegment(supplierFound, segment);
      toast.success(`✅ ${fournisseurRecommande} sélectionné (Abonnement: ${abonnementFutur}€/an, Prix moyen: P0 ${p0Moyen.toFixed(2)} + Marges)`);
    } else {
      toast.warning(`Fournisseur ${fournisseurRecommande} recommandé mais non trouvé dans la liste`);
    }
  };

  // useEffect APRÈS les définitions de fonctions
  useEffect(() => {
    fetchSuppliers();
    fetchSimulations();
  }, []);

  useEffect(() => {
    // Calculer automatiquement la date de fin (36 mois - 1 jour après le début)
    if (formData.date_debut_contrat) {
      const debut = new Date(formData.date_debut_contrat);
      const fin = new Date(debut);
      fin.setMonth(fin.getMonth() + parseInt(formData.duree_contrat_mois || 36));
      fin.setDate(fin.getDate() - 1); // Moins 1 jour
      setFormData(prev => ({
        ...prev,
        date_fin_contrat: fin.toISOString().split('T')[0]
      }));
    }
  }, [formData.date_debut_contrat, formData.duree_contrat_mois]);

  useEffect(() => {
    // Calcul en temps réel des résultats
    calculateResults();
  }, [
    formData.segment,
    formData.car,
    // Tous les champs CAR selon segments
    formData.car_hp, formData.car_hc,
    formData.car_pointe, formData.car_hph, formData.car_hch, formData.car_hpe, formData.car_hce,
    formData.car_base, formData.car_gaz,
    // Abonnements
    formData.abonnement_actuel_mensuel, formData.abonnement_futur_mensuel,
    // Prix MGW actuels
    formData.prix_actuel_hp, formData.prix_actuel_hc,
    formData.prix_actuel_pointe, formData.prix_actuel_hph, formData.prix_actuel_hch, formData.prix_actuel_hpe, formData.prix_actuel_hce,
    formData.prix_actuel_base, formData.prix_actuel_gaz,
    // Prix MGW futurs
    formData.prix_futur_hp, formData.prix_futur_hc,
    formData.prix_futur_pointe, formData.prix_futur_hph, formData.prix_futur_hch, formData.prix_futur_hpe, formData.prix_futur_hce,
    formData.prix_futur_base, formData.prix_futur_gaz,
    // Marge et durée
    formData.marge_mgw,
    formData.duree_contrat_mois
  ]);

  // Calcul automatique du Prix MGW Futur = P0 Fournisseur + Marge
  useEffect(() => {
    if (formData.fournisseur_futur_id) {
      const selectedSupplier = suppliers.find(s => s.id === formData.fournisseur_futur_id);
      if (selectedSupplier) {
        // Mettre à jour automatiquement selon le segment avec P0 et marges spécifiques
        if (formData.segment === "C5_HPHC") {
          // C5 HP/HC : 2 champs avec P0 et marge spécifiques
          setFormData(prev => ({
            ...prev,
            prix_futur_hp: (getP0ForSegment(selectedSupplier, formData.segment, 'hp') + getMargeForField('hp')).toFixed(2),
            prix_futur_hc: (getP0ForSegment(selectedSupplier, formData.segment, 'hc') + getMargeForField('hc')).toFixed(2)
          }));
        } else if (formData.segment === "C4") {
          // C4 : 4 champs avec P0 et marge spécifiques
          setFormData(prev => ({
            ...prev,
            prix_futur_hph: (getP0ForSegment(selectedSupplier, formData.segment, 'hph') + getMargeForField('hph')).toFixed(2),
            prix_futur_hch: (getP0ForSegment(selectedSupplier, formData.segment, 'hch') + getMargeForField('hch')).toFixed(2),
            prix_futur_hpe: (getP0ForSegment(selectedSupplier, formData.segment, 'hpe') + getMargeForField('hpe')).toFixed(2),
            prix_futur_hce: (getP0ForSegment(selectedSupplier, formData.segment, 'hce') + getMargeForField('hce')).toFixed(2)
          }));
        } else if (formData.segment === "C2") {
          // C2 : 5 champs avec P0 et marge spécifiques
          setFormData(prev => ({
            ...prev,
            prix_futur_pointe: (getP0ForSegment(selectedSupplier, formData.segment, 'pointe') + getMargeForField('pointe')).toFixed(2),
            prix_futur_hph: (getP0ForSegment(selectedSupplier, formData.segment, 'hph') + getMargeForField('hph')).toFixed(2),
            prix_futur_hch: (getP0ForSegment(selectedSupplier, formData.segment, 'hch') + getMargeForField('hch')).toFixed(2),
            prix_futur_hpe: (getP0ForSegment(selectedSupplier, formData.segment, 'hpe') + getMargeForField('hpe')).toFixed(2),
            prix_futur_hce: (getP0ForSegment(selectedSupplier, formData.segment, 'hce') + getMargeForField('hce')).toFixed(2)
          }));
        } else if (formData.segment === "C5_BASE") {
          // C5 BASE : 1 champ avec P0 et marge spécifiques
          setFormData(prev => ({
            ...prev,
            prix_futur_base: (getP0ForSegment(selectedSupplier, formData.segment, 'base') + getMargeForField('base')).toFixed(2)
          }));
        } else if (formData.segment === "GAZ") {
          // GAZ : 1 champ avec P0 et marge spécifiques
          setFormData(prev => ({
            ...prev,
            prix_futur_gaz: (getP0ForSegment(selectedSupplier, formData.segment, 'gaz') + getMargeForField('gaz')).toFixed(2)
          }));
        } else if (formData.segment === "C5_BASE") {
          // C5 BASE : 1 champ avec P0 spécifique
          setFormData(prev => ({
            ...prev,
            prix_futur_base: (getP0ForSegment(selectedSupplier, formData.segment, 'base') + marge).toFixed(2)
          }));
        } else if (formData.segment === "GAZ") {
          // GAZ : 1 champ avec P0 spécifique
          setFormData(prev => ({
            ...prev,
            prix_futur_gaz: (getP0ForSegment(selectedSupplier, formData.segment, 'gaz') + marge).toFixed(2)
          }));
        } else if (formData.segment === "C5_BASE") {
          // C5 BASE : 1 champ
          setFormData(prev => ({
            ...prev,
            prix_futur_base: prixCalcule.toFixed(2)
          }));
        } else if (formData.segment === "GAZ") {
          // GAZ : 1 champ
          setFormData(prev => ({
            ...prev,
            prix_futur_gaz: prixCalcule.toFixed(2)
          }));
        }
      }
    }
  }, [formData.fournisseur_futur_id, formData.segment, suppliers,
      formData.marge_hp, formData.marge_hc, 
      formData.marge_pointe, formData.marge_hph, formData.marge_hch, formData.marge_hpe, formData.marge_hce,
      formData.marge_base, formData.marge_gaz]);

  // useEffect APRÈS les définitions de fonctions

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.raison_sociale || !formData.siren || !formData.telephone) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Préparer les données pour l'API
      const simulationData = {
        ...formData,
        car: parseFloat(formData.car || formData.car_base || formData.car_gaz || 0),
        duree_contrat_mois: parseInt(formData.duree_contrat_mois),
        puissance_kva: formData.puissance_kva ? parseFloat(formData.puissance_kva) : null,
        abonnement_actuel_mensuel: parseFloat(formData.abonnement_actuel_mensuel || 0),
        abonnement_futur_mensuel: parseFloat(formData.abonnement_futur_mensuel || 0),
        score_ellipro: formData.score_ellipro ? parseFloat(formData.score_ellipro) : null,
        score_credit_safe: formData.score_credit_safe ? parseFloat(formData.score_credit_safe) : null,
        
        // Marges par segment
        marge_hp: formData.marge_hp ? parseFloat(formData.marge_hp) : null,
        marge_hc: formData.marge_hc ? parseFloat(formData.marge_hc) : null,
        marge_hph: formData.marge_hph ? parseFloat(formData.marge_hph) : null,
        marge_hch: formData.marge_hch ? parseFloat(formData.marge_hch) : null,
        marge_hpe: formData.marge_hpe ? parseFloat(formData.marge_hpe) : null,
        marge_hce: formData.marge_hce ? parseFloat(formData.marge_hce) : null,
        marge_pointe: formData.marge_pointe ? parseFloat(formData.marge_pointe) : null,
        marge_base: formData.marge_base ? parseFloat(formData.marge_base) : null,
        marge_gaz: formData.marge_gaz ? parseFloat(formData.marge_gaz) : null,
        
        // CAR detail - mapper vers les noms backend
        car_detail: {
          hp: formData.car_hp ? parseFloat(formData.car_hp) : null,
          hc: formData.car_hc ? parseFloat(formData.car_hc) : null,
          hph: formData.car_hph ? parseFloat(formData.car_hph) : null,
          hch: formData.car_hch ? parseFloat(formData.car_hch) : null,
          hpe: formData.car_hpe ? parseFloat(formData.car_hpe) : null,
          hce: formData.car_hce ? parseFloat(formData.car_hce) : null,
          pointe: formData.car_pointe ? parseFloat(formData.car_pointe) : null,
          base: formData.car_base ? parseFloat(formData.car_base) : null,
          consommation_gaz: formData.car_gaz ? parseFloat(formData.car_gaz) : null
        },
        
        // Prix MGW actuel - mapper vers les noms backend
        prix_mgw_actuel: {
          hp: formData.prix_actuel_hp ? parseFloat(formData.prix_actuel_hp) : null,
          hc: formData.prix_actuel_hc ? parseFloat(formData.prix_actuel_hc) : null,
          hph: formData.prix_actuel_hph ? parseFloat(formData.prix_actuel_hph) : null,
          hch: formData.prix_actuel_hch ? parseFloat(formData.prix_actuel_hch) : null,
          hpe: formData.prix_actuel_hpe ? parseFloat(formData.prix_actuel_hpe) : null,
          hce: formData.prix_actuel_hce ? parseFloat(formData.prix_actuel_hce) : null,
          pointe: formData.prix_actuel_pointe ? parseFloat(formData.prix_actuel_pointe) : null,
          base: formData.prix_actuel_base ? parseFloat(formData.prix_actuel_base) : null,
          gaz: formData.prix_actuel_gaz ? parseFloat(formData.prix_actuel_gaz) : null
        },
        
        // Prix MGW futur - mapper vers les noms backend
        prix_mgw_futur: {
          hp: formData.prix_futur_hp ? parseFloat(formData.prix_futur_hp) : null,
          hc: formData.prix_futur_hc ? parseFloat(formData.prix_futur_hc) : null,
          hph: formData.prix_futur_hph ? parseFloat(formData.prix_futur_hph) : null,
          hch: formData.prix_futur_hch ? parseFloat(formData.prix_futur_hch) : null,
          hpe: formData.prix_futur_hpe ? parseFloat(formData.prix_futur_hpe) : null,
          hce: formData.prix_futur_hce ? parseFloat(formData.prix_futur_hce) : null,
          pointe: formData.prix_futur_pointe ? parseFloat(formData.prix_futur_pointe) : null,
          base: formData.prix_futur_base ? parseFloat(formData.prix_futur_base) : null,
          gaz: formData.prix_futur_gaz ? parseFloat(formData.prix_futur_gaz) : null
        }
      };

      let response;
      if (editingSimulationId) {
        // Modification
        response = await axios.put(`${API}/simulations/${editingSimulationId}`, simulationData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Simulation modifiée avec succès");
      } else {
        // Création
        response = await axios.post(`${API}/simulations`, simulationData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Simulation créée avec succès");
      }

      // Rafraîchir la liste
      fetchSimulations();
      
      // Réinitialiser le formulaire
      resetForm();
    } catch (error) {
      console.error("Error saving simulation:", error);
      
      // Handle different error formats
      let errorMessage = "Erreur lors de la sauvegarde";
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // Handle Pydantic validation errors (array of error objects)
        if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map(err => {
            const field = err.loc ? err.loc.join('.') : 'Champ';
            return `${field}: ${err.msg}`;
          }).join(', ');
        }
        // Handle simple string error
        else if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        }
        // Handle object error
        else if (typeof errorData.detail === 'object') {
          errorMessage = JSON.stringify(errorData.detail);
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEditSimulation = (simulation) => {
    // Charger la simulation dans le formulaire
    setFormData({
      raison_sociale: simulation.raison_sociale || "",
      siren: simulation.siren || simulation.siret || "",  // Compatibilité ancien/nouveau
      interlocuteur: simulation.interlocuteur || "",
      fonction: simulation.fonction || "",
      telephone: simulation.telephone || "",
      adresse_site: simulation.adresse_site || "",
      code_postal: simulation.code_postal || "",
      ville: simulation.ville || "",
      segment: simulation.segment || "C5_BASE",
      date_debut_contrat: simulation.date_debut_contrat || "",
      date_fin_contrat: simulation.date_fin_contrat || "",
      duree_contrat_mois: simulation.duree_contrat_mois || 36,
      puissance_kva: simulation.puissance_kva || "",
      pdl: simulation.pdl || "",
      pce: simulation.pce || "",
      car: simulation.car || "",
      
      // CAR détaillé - nouveaux noms de champs
      car_hp: simulation.car_detail?.hp || "",
      car_hc: simulation.car_detail?.hc || "",
      car_hph: simulation.car_detail?.hph || "",
      car_hch: simulation.car_detail?.hch || "",
      car_hpe: simulation.car_detail?.hpe || "",
      car_hce: simulation.car_detail?.hce || "",
      car_pointe: simulation.car_detail?.pointe || "",
      car_base: simulation.car_detail?.base || "",
      car_gaz: simulation.car_detail?.consommation_gaz || "",
      
      // Fournisseurs
      fournisseur_actuel_id: simulation.fournisseur_actuel_id || "",
      fournisseur_actuel_nom: simulation.fournisseur_actuel_nom || "",
      fournisseur_actuel_logo: simulation.fournisseur_actuel_logo || "",
      fournisseur_futur_id: simulation.fournisseur_futur_id || "",
      fournisseur_futur_nom: simulation.fournisseur_futur_nom || "",
      
      // Abonnements
      abonnement_actuel_mensuel: simulation.abonnement_actuel_mensuel || "",
      abonnement_futur_mensuel: simulation.abonnement_futur_mensuel || "",
      
      // Prix MGW actuel - nouveaux noms
      prix_actuel_hp: simulation.prix_mgw_actuel?.hp || "",
      prix_actuel_hc: simulation.prix_mgw_actuel?.hc || "",
      prix_actuel_hph: simulation.prix_mgw_actuel?.hph || "",
      prix_actuel_hch: simulation.prix_mgw_actuel?.hch || "",
      prix_actuel_hpe: simulation.prix_mgw_actuel?.hpe || "",
      prix_actuel_hce: simulation.prix_mgw_actuel?.hce || "",
      prix_actuel_pointe: simulation.prix_mgw_actuel?.pointe || "",
      prix_actuel_base: simulation.prix_mgw_actuel?.base || "",
      prix_actuel_gaz: simulation.prix_mgw_actuel?.gaz || "",
      
      // Prix MGW futur - nouveaux noms
      prix_futur_hp: simulation.prix_mgw_futur?.hp || "",
      prix_futur_hc: simulation.prix_mgw_futur?.hc || "",
      prix_futur_hph: simulation.prix_mgw_futur?.hph || "",
      prix_futur_hch: simulation.prix_mgw_futur?.hch || "",
      prix_futur_hpe: simulation.prix_mgw_futur?.hpe || "",
      prix_futur_hce: simulation.prix_mgw_futur?.hce || "",
      prix_futur_pointe: simulation.prix_mgw_futur?.pointe || "",
      prix_futur_base: simulation.prix_mgw_futur?.base || "",
      prix_futur_gaz: simulation.prix_mgw_futur?.gaz || "",
      
      // Marges par segment
      marge_hp: simulation.marge_hp || "",
      marge_hc: simulation.marge_hc || "",
      marge_hph: simulation.marge_hph || "",
      marge_hch: simulation.marge_hch || "",
      marge_hpe: simulation.marge_hpe || "",
      marge_hce: simulation.marge_hce || "",
      marge_pointe: simulation.marge_pointe || "",
      marge_base: simulation.marge_base || "",
      marge_gaz: simulation.marge_gaz || "",
      
      // Scores
      score_ellipro: simulation.score_ellipro || "",
      score_credit_safe: simulation.score_credit_safe || ""
    });
    
    setEditingSimulationId(simulation.id);
    setActiveTab("new-simulation"); // Changer d'onglet vers le formulaire
    toast.info("Simulation chargée pour modification");
  };

  const handleDeleteSimulation = async (simulationId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette simulation ?")) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/simulations/${simulationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Simulation supprimée");
      fetchSimulations();
    } catch (error) {
      console.error("Error deleting simulation:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleDownloadPDF = async (simulationId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/simulations/${simulationId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob' // Important pour recevoir le PDF
      });
      
      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `simulation_${simulationId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF téléchargé avec succès");
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast.error("Erreur lors du téléchargement du PDF");
    }
  };

  const resetForm = () => {
    setFormData({
      raison_sociale: "",
      siren: "",
      interlocuteur: "",
      fonction: "",
      telephone: "",
      adresse_site: "",
      code_postal: "",
      ville: "",
      segment: "C5_BASE",
      date_debut_contrat: "",
      date_fin_contrat: "",
      duree_contrat_mois: 36,
      puissance_kva: "",
      pdl: "",
      pce: "",
      car: "",
      car_hp: "",
      car_hc: "",
      car_pointe: "",
      car_hph: "",
      car_hch: "",
      car_hpe: "",
      car_hce: "",
      car_base: "",
      car_gaz: "",
      fournisseur_actuel_nom: "",
      fournisseur_actuel_logo: null,
      fournisseur_futur_id: "",
      abonnement_actuel_mensuel: "",
      abonnement_futur_mensuel: "",
      prix_actuel_hp: "",
      prix_actuel_hc: "",
      prix_actuel_pointe: "",
      prix_actuel_hph: "",
      prix_actuel_hch: "",
      prix_actuel_hpe: "",
      prix_actuel_hce: "",
      prix_actuel_base: "",
      prix_actuel_gaz: "",
      prix_futur_hp: "",
      prix_futur_hc: "",
      prix_futur_pointe: "",
      prix_futur_hph: "",
      prix_futur_hch: "",
      prix_futur_hpe: "",
      prix_futur_hce: "",
      prix_futur_base: "",
      prix_futur_gaz: "",
      marge_hp: "",
      marge_hc: "",
      marge_pointe: "",
      marge_hph: "",
      marge_hch: "",
      marge_hpe: "",
      marge_hce: "",
      marge_base: "",
      marge_gaz: "",
      score_ellipro: "",
      score_credit_safe: ""
    });
    setEditingSimulationId(null);
    setCalculatedResults(null);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const renderCarFields = () => {
    const { segment } = formData;

    // C5 HP/HC (2 champs : HP et HC)
    if (segment === "C5_HPHC") {
      return (
        <div className="space-y-4">
          <h4 className="font-medium text-gray-900">Détail CAR (Heures Pleines / Heures Creuses)</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="car_hp">HP (MWh) *</Label>
              <Input
                id="car_hp"
                name="car_hp"
                type="number"
                step="0.001"
                value={formData.car_hp || ""}
                onChange={handleInputChange}
                placeholder="Ex: 80.000"
              />
            </div>
            <div>
              <Label htmlFor="car_hc">HC (MWh) *</Label>
              <Input
                id="car_hc"
                name="car_hc"
                type="number"
                step="0.001"
                value={formData.car_hc || ""}
                onChange={handleInputChange}
                placeholder="Ex: 55.000"
              />
            </div>
          </div>
        </div>
      );
    } 
    // C4 (4 champs : HPH, HCH, HPE, HCE)
    else if (segment === "C4") {
      return (
        <div className="space-y-4">
          <h4 className="font-medium text-gray-900">Détail CAR (4 Index)</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="car_hph">HPH - Heures Pleines Hiver (MWh) *</Label>
              <Input
                id="car_hph"
                name="car_hph"
                type="number"
                step="0.001"
                value={formData.car_hph || ""}
                onChange={handleInputChange}
                placeholder="Ex: 50.000"
              />
            </div>
            <div>
              <Label htmlFor="car_hch">HCH - Heures Creuses Hiver (MWh) *</Label>
              <Input
                id="car_hch"
                name="car_hch"
                type="number"
                step="0.001"
                value={formData.car_hch || ""}
                onChange={handleInputChange}
                placeholder="Ex: 30.000"
              />
            </div>
            <div>
              <Label htmlFor="car_hpe">HPE - Heures Pleines Été (MWh) *</Label>
              <Input
                id="car_hpe"
                name="car_hpe"
                type="number"
                step="0.001"
                value={formData.car_hpe || ""}
                onChange={handleInputChange}
                placeholder="Ex: 40.000"
              />
            </div>
            <div>
              <Label htmlFor="car_hce">HCE - Heures Creuses Été (MWh) *</Label>
              <Input
                id="car_hce"
                name="car_hce"
                type="number"
                step="0.001"
                value={formData.car_hce || ""}
                onChange={handleInputChange}
                placeholder="Ex: 25.000"
              />
            </div>
          </div>
        </div>
      );
    }
    // C2 (5 champs : POINTE, HPH, HCH, HPE, HCE)
    else if (segment === "C2") {
      return (
        <div className="space-y-4">
          <h4 className="font-medium text-gray-900">Détail CAR (5 Index avec Pointe)</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="car_pointe">POINTE (MWh) *</Label>
              <Input
                id="car_pointe"
                name="car_pointe"
                type="number"
                step="0.001"
                value={formData.car_pointe || ""}
                onChange={handleInputChange}
                placeholder="Ex: 10.000"
              />
            </div>
            <div>
              <Label htmlFor="car_hph">HPH - Heures Pleines Hiver (MWh) *</Label>
              <Input
                id="car_hph"
                name="car_hph"
                type="number"
                step="0.001"
                value={formData.car_hph || ""}
                onChange={handleInputChange}
                placeholder="Ex: 45.000"
              />
            </div>
            <div>
              <Label htmlFor="car_hch">HCH - Heures Creuses Hiver (MWh) *</Label>
              <Input
                id="car_hch"
                name="car_hch"
                type="number"
                step="0.001"
                value={formData.car_hch || ""}
                onChange={handleInputChange}
                placeholder="Ex: 25.000"
              />
            </div>
            <div>
              <Label htmlFor="car_hpe">HPE - Heures Pleines Été (MWh) *</Label>
              <Input
                id="car_hpe"
                name="car_hpe"
                type="number"
                step="0.001"
                value={formData.car_hpe || ""}
                onChange={handleInputChange}
                placeholder="Ex: 35.000"
              />
            </div>
            <div>
              <Label htmlFor="car_hce">HCE - Heures Creuses Été (MWh) *</Label>
              <Input
                id="car_hce"
                name="car_hce"
                type="number"
                step="0.001"
                value={formData.car_hce || ""}
                onChange={handleInputChange}
                placeholder="Ex: 20.000"
              />
            </div>
          </div>
        </div>
      );
    }
    // C5 BASE (1 champ : Base)
    else if (segment === "C5_BASE") {
      return (
        <div>
          <Label htmlFor="car_base">CAR - Consommation Annuelle de Référence (MWh) *</Label>
          <Input
            id="car_base"
            name="car_base"
            type="number"
            step="0.001"
            value={formData.car_base}
            onChange={handleInputChange}
            placeholder="Ex: 145.000"
          />
        </div>
      );
    } 
    // GAZ (1 champ : Gaz)
    else if (segment === "GAZ") {
      return (
        <div>
          <Label htmlFor="car_gaz">CAR Gaz - Consommation Annuelle (MWh) *</Label>
          <Input
            id="car_gaz"
            name="car_gaz"
            type="number"
            step="0.001"
            value={formData.car_gaz}
            onChange={handleInputChange}
            placeholder="Ex: 120.000"
          />
        </div>
      );
    }
  };

  const renderPrixMGWFields = (type) => {
    const { segment } = formData;
    const prefix = type === "actuel" ? "prix_actuel" : "prix_futur";

    // C5 HP/HC (2 champs : HP et HC)
    if (segment === "C5_HPHC") {
      return (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor={`${prefix}_hp`}>Prix HP (€/MWh) *</Label>
            <Input
              id={`${prefix}_hp`}
              name={`${prefix}_hp`}
              type="number"
              step="0.01"
              value={formData[`${prefix}_hp`] || ""}
              onChange={handleInputChange}
              placeholder="Ex: 85.50"
            />
          </div>
          <div>
            <Label htmlFor={`${prefix}_hc`}>Prix HC (€/MWh) *</Label>
            <Input
              id={`${prefix}_hc`}
              name={`${prefix}_hc`}
              type="number"
              step="0.01"
              value={formData[`${prefix}_hc`] || ""}
              onChange={handleInputChange}
              placeholder="Ex: 65.00"
            />
          </div>
        </div>
      );
    } 
    // C4 (4 champs : HPH, HCH, HPE, HCE)
    else if (segment === "C4") {
      return (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor={`${prefix}_hph`}>Prix HPH (€/MWh) *</Label>
            <Input
              id={`${prefix}_hph`}
              name={`${prefix}_hph`}
              type="number"
              step="0.01"
              value={formData[`${prefix}_hph`] || ""}
              onChange={handleInputChange}
              placeholder="Ex: 85.50"
            />
          </div>
          <div>
            <Label htmlFor={`${prefix}_hch`}>Prix HCH (€/MWh) *</Label>
            <Input
              id={`${prefix}_hch`}
              name={`${prefix}_hch`}
              type="number"
              step="0.01"
              value={formData[`${prefix}_hch`] || ""}
              onChange={handleInputChange}
              placeholder="Ex: 65.00"
            />
          </div>
          <div>
            <Label htmlFor={`${prefix}_hpe`}>Prix HPE (€/MWh) *</Label>
            <Input
              id={`${prefix}_hpe`}
              name={`${prefix}_hpe`}
              type="number"
              step="0.01"
              value={formData[`${prefix}_hpe`] || ""}
              onChange={handleInputChange}
              placeholder="Ex: 80.00"
            />
          </div>
          <div>
            <Label htmlFor={`${prefix}_hce`}>Prix HCE (€/MWh) *</Label>
            <Input
              id={`${prefix}_hce`}
              name={`${prefix}_hce`}
              type="number"
              step="0.01"
              value={formData[`${prefix}_hce`] || ""}
              onChange={handleInputChange}
              placeholder="Ex: 60.00"
            />
          </div>
        </div>
      );
    }
    // C2 (5 champs : POINTE, HPH, HCH, HPE, HCE)
    else if (segment === "C2") {
      return (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor={`${prefix}_pointe`}>Prix POINTE (€/MWh) *</Label>
            <Input
              id={`${prefix}_pointe`}
              name={`${prefix}_pointe`}
              type="number"
              step="0.01"
              value={formData[`${prefix}_pointe`] || ""}
              onChange={handleInputChange}
              placeholder="Ex: 95.00"
            />
          </div>
          <div>
            <Label htmlFor={`${prefix}_hph`}>Prix HPH (€/MWh) *</Label>
            <Input
              id={`${prefix}_hph`}
              name={`${prefix}_hph`}
              type="number"
              step="0.01"
              value={formData[`${prefix}_hph`] || ""}
              onChange={handleInputChange}
              placeholder="Ex: 85.50"
            />
          </div>
          <div>
            <Label htmlFor={`${prefix}_hch`}>Prix HCH (€/MWh) *</Label>
            <Input
              id={`${prefix}_hch`}
              name={`${prefix}_hch`}
              type="number"
              step="0.01"
              value={formData[`${prefix}_hch`] || ""}
              onChange={handleInputChange}
              placeholder="Ex: 65.00"
            />
          </div>
          <div>
            <Label htmlFor={`${prefix}_hpe`}>Prix HPE (€/MWh) *</Label>
            <Input
              id={`${prefix}_hpe`}
              name={`${prefix}_hpe`}
              type="number"
              step="0.01"
              value={formData[`${prefix}_hpe`] || ""}
              onChange={handleInputChange}
              placeholder="Ex: 80.00"
            />
          </div>
          <div>
            <Label htmlFor={`${prefix}_hce`}>Prix HCE (€/MWh) *</Label>
            <Input
              id={`${prefix}_hce`}
              name={`${prefix}_hce`}
              type="number"
              step="0.01"
              value={formData[`${prefix}_hce`] || ""}
              onChange={handleInputChange}
              placeholder="Ex: 60.00"
            />
          </div>
        </div>
      );
    }
    // C5 BASE (1 champ : Base)
    else if (segment === "C5_BASE") {
      return (
        <div>
          <Label htmlFor={`${prefix}_base`}>Prix Base (€/MWh) *</Label>
          <Input
            id={`${prefix}_base`}
            name={`${prefix}_base`}
            type="number"
            step="0.01"
            value={formData[`${prefix}_base`] || ""}
            onChange={handleInputChange}
            placeholder="Ex: 75.00"
          />
        </div>
      );
    } 
    // GAZ (1 champ : Gaz)
    else if (segment === "GAZ") {
      return (
        <div>
          <Label htmlFor={`${prefix}_gaz`}>Prix Gaz (€/MWh) *</Label>
          <Input
            id={`${prefix}_gaz`}
            name={`${prefix}_gaz`}
            type="number"
            step="0.01"
            value={formData[`${prefix}_gaz`] || ""}
            onChange={handleInputChange}
            placeholder="Ex: 45.00"
          />
        </div>
      );
    }
  };

  const renderMargeFields = () => {
    const { segment } = formData;

    // C5 HP/HC (2 champs : HP et HC)
    if (segment === "C5_HPHC") {
      return (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="marge_hp">Marge HP (€/MWh) *</Label>
            <Input
              id="marge_hp"
              name="marge_hp"
              type="number"
              step="0.01"
              value={formData.marge_hp || ""}
              onChange={handleInputChange}
              placeholder="Ex: 5.00"
            />
          </div>
          <div>
            <Label htmlFor="marge_hc">Marge HC (€/MWh) *</Label>
            <Input
              id="marge_hc"
              name="marge_hc"
              type="number"
              step="0.01"
              value={formData.marge_hc || ""}
              onChange={handleInputChange}
              placeholder="Ex: 5.00"
            />
          </div>
        </div>
      );
    } 
    // C4 (4 champs : HPH, HCH, HPE, HCE)
    else if (segment === "C4") {
      return (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="marge_hph">Marge HPH (€/MWh) *</Label>
            <Input
              id="marge_hph"
              name="marge_hph"
              type="number"
              step="0.01"
              value={formData.marge_hph || ""}
              onChange={handleInputChange}
              placeholder="Ex: 5.00"
            />
          </div>
          <div>
            <Label htmlFor="marge_hch">Marge HCH (€/MWh) *</Label>
            <Input
              id="marge_hch"
              name="marge_hch"
              type="number"
              step="0.01"
              value={formData.marge_hch || ""}
              onChange={handleInputChange}
              placeholder="Ex: 5.00"
            />
          </div>
          <div>
            <Label htmlFor="marge_hpe">Marge HPE (€/MWh) *</Label>
            <Input
              id="marge_hpe"
              name="marge_hpe"
              type="number"
              step="0.01"
              value={formData.marge_hpe || ""}
              onChange={handleInputChange}
              placeholder="Ex: 5.00"
            />
          </div>
          <div>
            <Label htmlFor="marge_hce">Marge HCE (€/MWh) *</Label>
            <Input
              id="marge_hce"
              name="marge_hce"
              type="number"
              step="0.01"
              value={formData.marge_hce || ""}
              onChange={handleInputChange}
              placeholder="Ex: 5.00"
            />
          </div>
        </div>
      );
    }
    // C2 (5 champs : POINTE, HPH, HCH, HPE, HCE)
    else if (segment === "C2") {
      return (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="marge_pointe">Marge POINTE (€/MWh) *</Label>
            <Input
              id="marge_pointe"
              name="marge_pointe"
              type="number"
              step="0.01"
              value={formData.marge_pointe || ""}
              onChange={handleInputChange}
              placeholder="Ex: 5.00"
            />
          </div>
          <div>
            <Label htmlFor="marge_hph">Marge HPH (€/MWh) *</Label>
            <Input
              id="marge_hph"
              name="marge_hph"
              type="number"
              step="0.01"
              value={formData.marge_hph || ""}
              onChange={handleInputChange}
              placeholder="Ex: 5.00"
            />
          </div>
          <div>
            <Label htmlFor="marge_hch">Marge HCH (€/MWh) *</Label>
            <Input
              id="marge_hch"
              name="marge_hch"
              type="number"
              step="0.01"
              value={formData.marge_hch || ""}
              onChange={handleInputChange}
              placeholder="Ex: 5.00"
            />
          </div>
          <div>
            <Label htmlFor="marge_hpe">Marge HPE (€/MWh) *</Label>
            <Input
              id="marge_hpe"
              name="marge_hpe"
              type="number"
              step="0.01"
              value={formData.marge_hpe || ""}
              onChange={handleInputChange}
              placeholder="Ex: 5.00"
            />
          </div>
          <div>
            <Label htmlFor="marge_hce">Marge HCE (€/MWh) *</Label>
            <Input
              id="marge_hce"
              name="marge_hce"
              type="number"
              step="0.01"
              value={formData.marge_hce || ""}
              onChange={handleInputChange}
              placeholder="Ex: 5.00"
            />
          </div>
        </div>
      );
    }
    // C5 BASE (1 champ : Base)
    else if (segment === "C5_BASE") {
      return (
        <div>
          <Label htmlFor="marge_base">Marge à appliquer au MGW (€/MWh) *</Label>
          <Input
            id="marge_base"
            name="marge_base"
            type="number"
            step="0.01"
            value={formData.marge_base || ""}
            onChange={handleInputChange}
            placeholder="Ex: 5.00"
          />
        </div>
      );
    } 
    // GAZ (1 champ : Gaz)
    else if (segment === "GAZ") {
      return (
        <div>
          <Label htmlFor="marge_gaz">Marge à appliquer au MGW (€/MWh) *</Label>
          <Input
            id="marge_gaz"
            name="marge_gaz"
            type="number"
            step="0.01"
            value={formData.marge_gaz || ""}
            onChange={handleInputChange}
            placeholder="Ex: 5.00"
          />
        </div>
      );
    }
  };

  return (
    <div className="p-8 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Simulateur d'Offres Énergétiques</h1>
          <p className="text-gray-600 mt-1">Créez des études comparatives complètes selon les segments C4, C5, C2 et GAZ</p>
        </div>
        <div className="flex items-center space-x-2 text-emerald-600">
          <Calculator className="w-6 h-6" />
          <span className="font-medium">Outil de simulation</span>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="new-simulation" className="flex items-center space-x-2">
            <FileText className="w-4 h-4" />
            <span>Nouvelle Simulation</span>
          </TabsTrigger>
          <TabsTrigger value="saved-simulations" className="flex items-center space-x-2">
            <Save className="w-4 h-4" />
            <span>Simulations Sauvegardées ({simulations.length})</span>
          </TabsTrigger>
        </TabsList>

        {/* Nouvelle Simulation */}
        <TabsContent value="new-simulation">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Section 1: Informations Client */}
            <Card>
              <CardHeader>
                <CardTitle>1. Informations Client</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="siren">SIREN (Recherche automatique) *</Label>
                    <SirenSearch
                      value={formData.siren}
                      onChange={(value) => setFormData(prev => ({ ...prev, siren: value }))}
                      onCompanySelect={handleSirenCompanySelect}
                    />
                  </div>
                  <div>
                    <Label htmlFor="raison_sociale">Raison Sociale *</Label>
                    <Input
                      id="raison_sociale"
                      name="raison_sociale"
                      value={formData.raison_sociale}
                      onChange={handleInputChange}
                      placeholder="Ex: BOULANGERIE MARTIN"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="interlocuteur">Interlocuteur *</Label>
                    <Input
                      id="interlocuteur"
                      name="interlocuteur"
                      value={formData.interlocuteur}
                      onChange={handleInputChange}
                      placeholder="Ex: Jean MARTIN"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="fonction">Fonction</Label>
                    <Input
                      id="fonction"
                      name="fonction"
                      value={formData.fonction}
                      onChange={handleInputChange}
                      placeholder="Ex: Gérant"
                    />
                  </div>
                  <div>
                    <Label htmlFor="telephone">Téléphone *</Label>
                    <Input
                      id="telephone"
                      name="telephone"
                      value={formData.telephone}
                      onChange={handleInputChange}
                      placeholder="Ex: 06 12 34 56 78"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="adresse_site">Adresse du site *</Label>
                    <Input
                      id="adresse_site"
                      name="adresse_site"
                      value={formData.adresse_site}
                      onChange={handleInputChange}
                      placeholder="Ex: 123 Rue de la République"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="code_postal">Code Postal</Label>
                    <Input
                      id="code_postal"
                      name="code_postal"
                      value={formData.code_postal}
                      onChange={handleInputChange}
                      placeholder="Ex: 75001"
                    />
                  </div>
                  <div>
                    <Label htmlFor="ville">Ville</Label>
                    <Input
                      id="ville"
                      name="ville"
                      value={formData.ville}
                      onChange={handleInputChange}
                      placeholder="Ex: Paris"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Section 2: Segment et Caractéristiques */}
            <Card>
              <CardHeader>
                <CardTitle>2. Segment et Caractéristiques Techniques</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="segment">Segment *</Label>
                    <Select value={formData.segment} onValueChange={(value) => handleSelectChange("segment", value)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="C5_BASE">C5 Base (Tarif de base)</SelectItem>
                        <SelectItem value="C5_HPHC">C5 HPHC (Heures Creuses/Pleines)</SelectItem>
                        <SelectItem value="C4">C4</SelectItem>
                        <SelectItem value="C2">C2</SelectItem>
                        <SelectItem value="GAZ">GAZ</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {formData.segment !== "GAZ" && (
                    <>
                      <div>
                        <Label htmlFor="puissance_kva">Puissance (KVA)</Label>
                        <Input
                          id="puissance_kva"
                          name="puissance_kva"
                          type="number"
                          step="0.1"
                          value={formData.puissance_kva}
                          onChange={handleInputChange}
                          placeholder="Ex: 36.0"
                        />
                      </div>
                      <div>
                        <Label htmlFor="pdl">N° PDL (Point de Livraison)</Label>
                        <Input
                          id="pdl"
                          name="pdl"
                          value={formData.pdl}
                          onChange={handleInputChange}
                          placeholder="Ex: 12345678901234"
                        />
                      </div>
                    </>
                  )}
                  {formData.segment === "GAZ" && (
                    <div>
                      <Label htmlFor="pce">N° PCE (Point de Comptage et Estimation)</Label>
                      <Input
                        id="pce"
                        name="pce"
                        value={formData.pce}
                        onChange={handleInputChange}
                        placeholder="Ex: GI123456"
                      />
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Section 3: Dates et Durée Contrat */}
            <Card>
              <CardHeader>
                <CardTitle>3. Dates et Durée du Contrat</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="date_debut_contrat">Date de Début *</Label>
                    <Input
                      id="date_debut_contrat"
                      name="date_debut_contrat"
                      type="date"
                      value={formData.date_debut_contrat}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="duree_contrat_mois">Durée (Mois) *</Label>
                    <Input
                      id="duree_contrat_mois"
                      name="duree_contrat_mois"
                      type="number"
                      value={formData.duree_contrat_mois}
                      onChange={handleInputChange}
                      placeholder="36"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="date_fin_contrat">Date de Fin (Calculée)</Label>
                    <Input
                      id="date_fin_contrat"
                      name="date_fin_contrat"
                      type="date"
                      value={formData.date_fin_contrat}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Section 4: Consommation (CAR) */}
            <Card>
              <CardHeader>
                <CardTitle>4. Consommation Annuelle de Référence (CAR)</CardTitle>
              </CardHeader>
              <CardContent>
                {renderCarFields()}
              </CardContent>
            </Card>

            {/* Section 5: Fournisseur Actuel */}
            <Card>
              <CardHeader>
                <CardTitle>5. Fournisseur Actuel</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="fournisseur_actuel_nom">Nom du Fournisseur Actuel *</Label>
                    <Input
                      id="fournisseur_actuel_nom"
                      name="fournisseur_actuel_nom"
                      value={formData.fournisseur_actuel_nom}
                      onChange={handleInputChange}
                      placeholder="Ex: EDF"
                      required
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="fournisseur_actuel_logo">Logo du Fournisseur Actuel</Label>
                    <div 
                      className={`mt-1 border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                        isDragging 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                    >
                      {formData.fournisseur_actuel_logo ? (
                        <div className="flex items-center justify-center space-x-4">
                          <img 
                            src={formData.fournisseur_actuel_logo} 
                            alt="Logo" 
                            className="h-20 object-contain"
                          />
                          <div className="flex flex-col space-y-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => document.getElementById('fournisseur_actuel_logo').click()}
                            >
                              Changer le logo
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => setFormData(prev => ({ ...prev, fournisseur_actuel_logo: null }))}
                            >
                              Supprimer
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <Upload className="w-8 h-8 mx-auto text-gray-400" />
                          <div className="text-sm text-gray-600">
                            <label htmlFor="fournisseur_actuel_logo" className="cursor-pointer text-blue-600 hover:text-blue-700 font-medium">
                              Cliquez pour choisir
                            </label>
                            {' '}ou glissez-déposez un logo
                          </div>
                          <p className="text-xs text-gray-500">PNG, JPG, GIF jusqu'à 10MB</p>
                        </div>
                      )}
                      <Input
                        id="fournisseur_actuel_logo"
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="abonnement_actuel_mensuel">Abonnement Mensuel (€) *</Label>
                    <Input
                      id="abonnement_actuel_mensuel"
                      name="abonnement_actuel_mensuel"
                      type="number"
                      step="0.01"
                      value={formData.abonnement_actuel_mensuel}
                      onChange={handleInputChange}
                      placeholder="Ex: 150.00"
                      required
                    />
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Prix MGW Actuel</h4>
                  {renderPrixMGWFields("actuel")}
                </div>
              </CardContent>
            </Card>

            {/* Section 6: Marge et Scoring (ancienne section 7) */}
            <Card>
              <CardHeader>
                <CardTitle>6. Marge et Scoring</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Marges selon segment */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Marge à appliquer selon le segment</h4>
                  {renderMargeFields()}
                </div>
                
                {/* Scores */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <Label htmlFor="score_ellipro">Score Ellipro (0-10)</Label>
                    <Input
                      id="score_ellipro"
                      name="score_ellipro"
                      type="number"
                      step="0.1"
                      min="0"
                      max="10"
                      value={formData.score_ellipro}
                      onChange={handleInputChange}
                      placeholder="Ex: 7.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="score_credit_safe">Score Credit Safe (0-100)</Label>
                    <Input
                      id="score_credit_safe"
                      name="score_credit_safe"
                      type="number"
                      step="1"
                      min="0"
                      max="100"
                      value={formData.score_credit_safe}
                      onChange={handleInputChange}
                      placeholder="Ex: 75"
                    />
                  </div>
                </div>
                
                {/* Bouton pour calculer la recommandation */}
                <div className="flex justify-center pt-4">
                  <Button
                    type="button"
                    onClick={handleCalculateRecommandation}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Calculator className="w-4 h-4 mr-2" />
                    Calculer le Fournisseur Recommandé
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Recommandation Fournisseur (affichée après calcul) */}
            {recommendation && (
              <Card className="border-blue-200 bg-blue-50">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-blue-600 mt-1" />
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-blue-900">
                        Fournisseur Recommandé : {recommendation.fournisseur}
                      </h3>
                      <p className="text-blue-700 mt-1">{recommendation.raison}</p>
                      <p className="text-sm text-blue-600 mt-2">
                        <strong>Modalités de paiement :</strong> {recommendation.modalites}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Section 7: Fournisseur Futur (ancienne section 6) */}
            <Card>
              <CardHeader>
                <CardTitle>7. Fournisseur Futur (Offre Proposée)</CardTitle>
                <CardDescription>
                  Choisissez le fournisseur que vous souhaitez proposer au client
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="fournisseur_futur_id">Fournisseur *</Label>
                    <Select value={formData.fournisseur_futur_id} onValueChange={(value) => handleSelectChange("fournisseur_futur_id", value)} required>
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner un fournisseur" />
                      </SelectTrigger>
                      <SelectContent>
                        {suppliers.map(s => (
                          <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="abonnement_futur_mensuel">Abonnement Mensuel (€) *</Label>
                    <Input
                      id="abonnement_futur_mensuel"
                      name="abonnement_futur_mensuel"
                      type="number"
                      step="0.01"
                      value={formData.abonnement_futur_mensuel}
                      onChange={handleInputChange}
                      placeholder="Ex: 140.00"
                      required
                    />
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Prix MGW Futur</h4>
                  {renderPrixMGWFields("futur")}
                </div>
              </CardContent>
            </Card>

            {/* Résultats en Temps Réel */}
            {calculatedResults && (
              <Card className="border-emerald-200 bg-emerald-50">
                <CardHeader>
                  <CardTitle className="text-emerald-900">Résultats de la Simulation</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="bg-white p-4 rounded-lg">
                      <div className="text-sm text-gray-600">Budget Annuel Actuel</div>
                      <div className="text-xl font-bold text-gray-900 mt-1">
                        {formatCurrency(calculatedResults.budget_actuel)}
                      </div>
                    </div>
                    <div className="bg-white p-4 rounded-lg">
                      <div className="text-sm text-gray-600">Budget Annuel Futur</div>
                      <div className="text-xl font-bold text-blue-600 mt-1">
                        {formatCurrency(calculatedResults.budget_futur)}
                      </div>
                    </div>
                    <div className="bg-white p-4 rounded-lg">
                      <div className="text-sm text-gray-600">Économie Annuelle</div>
                      <div className="text-xl font-bold text-emerald-600 mt-1">
                        {formatCurrency(calculatedResults.economie_annuelle)}
                      </div>
                    </div>
                    <div className="bg-white p-4 rounded-lg">
                      <div className="text-sm text-gray-600">Économie Globale ({(formData.duree_contrat_mois || 36) / 12} ans)</div>
                      <div className="text-xl font-bold text-emerald-600 mt-1">
                        {formatCurrency(calculatedResults.economie_globale)}
                      </div>
                    </div>
                    <div className="bg-white p-4 rounded-lg">
                      <div className="text-sm text-gray-600">Variation %</div>
                      <div className="text-xl font-bold text-purple-600 mt-1">
                        {calculatedResults.variation_pourcentage.toFixed(2)}%
                      </div>
                    </div>
                    <div className="bg-white p-4 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-600">Commission Calculée</div>
                        <button
                          type="button"
                          onClick={() => setShowCommission(!showCommission)}
                          className="p-1 hover:bg-gray-100 rounded transition-colors"
                          title={showCommission ? "Masquer commission" : "Afficher commission"}
                        >
                          {showCommission ? (
                            <Eye className="w-4 h-4 text-gray-600" />
                          ) : (
                            <EyeOff className="w-4 h-4 text-gray-600" />
                          )}
                        </button>
                      </div>
                      <div className="text-xl font-bold text-amber-600 mt-1">
                        {showCommission ? formatCurrency(calculatedResults.commission) : "•••••"}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Boutons d'action */}
            <div className="flex justify-between items-center">
              <Button
                type="button"
                variant="outline"
                onClick={resetForm}
                disabled={loading}
              >
                Réinitialiser
              </Button>
              <div className="flex space-x-3">
                <Button
                  type="submit"
                  className="bg-emerald-600 hover:bg-emerald-700"
                  disabled={loading}
                >
                  <Save className="w-4 h-4 mr-2" />
                  {loading ? "Enregistrement..." : editingSimulationId ? "Modifier la Simulation" : "Sauvegarder la Simulation"}
                </Button>
              </div>
            </div>
          </form>
        </TabsContent>

        {/* Simulations Sauvegardées */}
        <TabsContent value="saved-simulations">
          <Card>
            <CardHeader>
              <CardTitle>Simulations Sauvegardées</CardTitle>
              <CardDescription>Liste de toutes vos simulations créées</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Filtres */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  {/* Recherche */}
                  <div>
                    <Label htmlFor="filter-search" className="text-sm">Rechercher</Label>
                    <Input
                      id="filter-search"
                      type="text"
                      placeholder="Raison sociale, SIREN..."
                      value={filters.search}
                      onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                  
                  {/* Segment */}
                  <div>
                    <Label htmlFor="filter-segment" className="text-sm">Segment</Label>
                    <Select
                      value={filters.segment}
                      onValueChange={(value) => setFilters(prev => ({ ...prev, segment: value }))}
                    >
                      <SelectTrigger id="filter-segment" className="mt-1">
                        <SelectValue placeholder="Tous les segments" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tous les segments</SelectItem>
                        <SelectItem value="C5_BASE">C5 BASE</SelectItem>
                        <SelectItem value="C5_HPHC">C5 HP/HC</SelectItem>
                        <SelectItem value="C4">C4</SelectItem>
                        <SelectItem value="C2">C2</SelectItem>
                        <SelectItem value="GAZ">GAZ</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Date de début */}
                  <div>
                    <Label htmlFor="filter-date-from" className="text-sm">Date de création (début)</Label>
                    <Input
                      id="filter-date-from"
                      type="date"
                      value={filters.dateFrom}
                      onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                  
                  {/* Date de fin */}
                  <div>
                    <Label htmlFor="filter-date-to" className="text-sm">Date de création (fin)</Label>
                    <Input
                      id="filter-date-to"
                      type="date"
                      value={filters.dateTo}
                      onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                </div>
                
                {/* Bouton reset filtres */}
                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setFilters({ search: "", segment: "all", dateFrom: "", dateTo: "" })}
                  >
                    Réinitialiser les filtres
                  </Button>
                </div>
              </div>
              
              {loadingSimulations ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto"></div>
                  <p className="text-gray-500 mt-4">Chargement...</p>
                </div>
              ) : (() => {
                // Filtrer les simulations
                const filteredSimulations = simulations.filter(sim => {
                  // Filtre recherche
                  if (filters.search) {
                    const searchLower = filters.search.toLowerCase();
                    const matchesSearch = 
                      sim.raison_sociale?.toLowerCase().includes(searchLower) ||
                      sim.siren?.toLowerCase().includes(searchLower) ||
                      sim.siret?.toLowerCase().includes(searchLower);
                    if (!matchesSearch) return false;
                  }
                  
                  // Filtre segment
                  if (filters.segment !== "all" && sim.segment !== filters.segment) {
                    return false;
                  }
                  
                  // Filtre date de début
                  if (filters.dateFrom) {
                    const simDate = new Date(sim.created_at);
                    const filterDate = new Date(filters.dateFrom);
                    if (simDate < filterDate) return false;
                  }
                  
                  // Filtre date de fin
                  if (filters.dateTo) {
                    const simDate = new Date(sim.created_at);
                    const filterDate = new Date(filters.dateTo);
                    filterDate.setHours(23, 59, 59); // Fin de journée
                    if (simDate > filterDate) return false;
                  }
                  
                  return true;
                });
                
                return filteredSimulations.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      {simulations.length === 0 ? "Aucune simulation" : "Aucun résultat"}
                    </h3>
                    <p className="text-gray-500">
                      {simulations.length === 0 
                        ? "Créez votre première simulation dans l'onglet \"Nouvelle Simulation\""
                        : "Essayez de modifier vos critères de filtre"
                      }
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-500">
                      {filteredSimulations.length} simulation(s) trouvée(s) sur {simulations.length} au total
                    </p>
                    {filteredSimulations.map((sim) => (
                    <Card key={sim.id} className="border-l-4 border-l-emerald-500">
                      <CardContent className="p-6">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-gray-900">{sim.raison_sociale}</h3>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 text-sm">
                              <div>
                                <span className="text-gray-500">Segment:</span>
                                <div className="font-medium">{sim.segment}</div>
                              </div>
                              <div>
                                <span className="text-gray-500">CAR:</span>
                                <div className="font-medium">{sim.car} MWh</div>
                              </div>
                              <div>
                                <span className="text-gray-500">Économie annuelle:</span>
                                <div className="font-medium text-emerald-600">
                                  {formatCurrency(sim.economie_annuelle)}
                                </div>
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="text-gray-500">Commission:</span>
                                  <button
                                    type="button"
                                    onClick={() => setShowCommission(!showCommission)}
                                    className="p-0.5 hover:bg-gray-100 rounded transition-colors"
                                    title={showCommission ? "Masquer commission" : "Afficher commission"}
                                  >
                                    {showCommission ? (
                                      <Eye className="w-3.5 h-3.5 text-gray-600" />
                                    ) : (
                                      <EyeOff className="w-3.5 h-3.5 text-gray-600" />
                                    )}
                                  </button>
                                </div>
                                <div className="font-medium text-amber-600">
                                  {showCommission ? formatCurrency(sim.commission_calculee) : "•••••"}
                                </div>
                              </div>
                            </div>
                            {sim.fournisseur_recommande && (
                              <div className="mt-3">
                                <Badge className="bg-blue-100 text-blue-800">
                                  Recommandé: {sim.fournisseur_recommande}
                                </Badge>
                              </div>
                            )}
                          </div>
                          <div className="flex space-x-2 ml-4">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEditSimulation(sim)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => handleDeleteSimulation(sim.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              className="bg-blue-600 hover:bg-blue-700"
                              onClick={() => handleDownloadPDF(sim.id)}
                            >
                              <Download className="w-4 h-4 mr-1" />
                              PDF
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              );
            })()}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SimulatorPage;
